'use client';

import React, { useEffect, useState } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useStickySectionScroll } from '@/app/hooks/useStickySectionScroll';
import {
  resetProductFilters,
  updateFilters,
} from '@/redux/reducers/products-reducer';
import { cn } from '@/utils/cn';
import { useDispatch, useSelector } from 'react-redux';
import { getProductBrandUrl } from '@/url/product';
import { defaultAll } from '@/constants/list';
import SubCategoriesFilter from './SubCategoriesFilter';
import FilterSortControlBar from './FilterSortControlBar';

function ProductFilterNavigationBar({
  subCatMenu = true,
  totalProductsCount,
  className = '',
  isCategory = true,
}) {
  const dispatch = useDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { slug } = useParams();

  const subCategory = searchParams.get('category') ?? '';
  const brandSlug = !isCategory ? (searchParams.get('brand') ?? slug) : '';

  const { current } = useSelector((state) => state.categories);

  // hooks
  // scroll on for plp page
  const isSectionSticky = useStickySectionScroll();

  // states
  const [openMenu, setOpenMenu] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const removeDuplicateQueryParams = (url) => {
    const [baseUrl, queryString] = url.split('?');
    if (!queryString) return url;

    const params = new URLSearchParams(queryString);
    const uniqueParams = new Map();

    params.forEach((value, key) => {
      if (!uniqueParams.has(key)) {
        uniqueParams.set(key, value);
      }
    });

    return `${baseUrl}?${new URLSearchParams(uniqueParams).toString()}`;
  };

  const handleFilterProducts = async ({
    e,
    category = '',
    subCategorySlug = '',
  }) => {
    dispatch(resetProductFilters());

    if (subCategorySlug) {
      e.stopPropagation();
    }

    const params = new URLSearchParams();
    const categorySlug = subCategorySlug || category;

    if (!isCategory) {
      params.set('brand', brandSlug);
    } else {
      params.delete('brand');
    }

    if (categorySlug) {
      params.set('category', categorySlug);
    } else {
      params.delete('category');
    }

    params.set('page', '1');
    params.set('limit', 12);

    setActiveItem(categorySlug);

    // Update store filters
    dispatch(
      updateFilters({
        category: categorySlug,
        page: 1,
        limit: 12,
        brand: !isCategory ? brandSlug : null,
      }),
    );

    //  PARAMS FOR BRAND PARAMS UNIQUE
    if (!isCategory) {
      // Update the url
      const url = removeDuplicateQueryParams(
        getProductBrandUrl({ slug: brandSlug, paramsString: params }),
      );

      router.push(url);
      return;
    }

    // Update the url
    const url = `/category/${category}?${params.toString()}`;

    router.push(url);
  };

  const handleMenuOpen = () => {
    setOpenMenu(!openMenu);
  };

  const handleScroll = () => {
    if (openMenu) {
      handleMenuOpen();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [openMenu]);

  useEffect(() => {
    if (subCategory) {
      setActiveItem(subCategory);
    } else {
      setActiveItem(isCategory ? current?.slug : defaultAll?.slug);
    }
  }, [subCategory, current?.slug]);

  return (
    <div
      className={cn(
        'product-filter-navigation-bar bg-white relative transition-all duration-300',
        className,
        {
          'sticky top-[94px] lg:top-[140px] z-[198] transition-all duration-500 ease-in-out':
            isSectionSticky,
        },
      )}
    >
      <SubCategoriesFilter
        subCatMenu={subCatMenu}
        openMenu={openMenu}
        handleFilterProducts={handleFilterProducts}
        handleMenuOpen={handleMenuOpen}
        activeItem={activeItem}
        isCategory={isCategory}
      />

      <FilterSortControlBar
        totalProductsCount={totalProductsCount}
        openMenu={openMenu}
        isCategory={isCategory}
      />
    </div>
  );
}

export default ProductFilterNavigationBar;
